import React, { useState } from 'react';
import { Button, Descriptions, Table } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { useGetFetch } from '../../utils/reactQuery';

import { CandidateConsent, CandidateConsentStatus, consentTypeMap } from './Consents.types';
import { ConsentTemplatePreview } from './ConsentTemplatePreview';
import { FormattedCandidateConsentStatus } from './FormattedCandidateConsentStatus';

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 992px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

export const CandidateConsentHistory: React.FC<{ candidateConsent?: CandidateConsent }> = ({ candidateConsent }) => {
  const { data } = useGetFetch<CandidateConsent[]>({
    url: `/candidate/consent/history/${candidateConsent?.originalId}`,
    config: { enabled: !!candidateConsent },
  });

  const [showContent, setShowContent] = useState(false);

  if (!candidateConsent) {
    return null;
  }

  return (
    <>
      <StyledHeader>
        <Descriptions colon={false} column={1}>
          {candidateConsent.client && (
            <Descriptions.Item label="Client" labelStyle={{ width: 100 }} contentStyle={{ fontWeight: 600 }}>
              {candidateConsent.client.name}
            </Descriptions.Item>
          )}
          {candidateConsent.project && (
            <Descriptions.Item label="Project" labelStyle={{ width: 100 }} contentStyle={{ fontWeight: 600 }}>
              {candidateConsent.project.name}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Consent type" labelStyle={{ width: 100 }} contentStyle={{ fontWeight: 600 }}>
            {consentTypeMap[candidateConsent.type]}
          </Descriptions.Item>
        </Descriptions>

        <Button style={{ marginBottom: '10px' }} onClick={() => setShowContent((prevState) => !prevState)}>
          {showContent ? 'Hide content' : 'Show content'}
        </Button>
      </StyledHeader>

      {showContent ? (
        <ConsentTemplatePreview
          consentTemplate={candidateConsent.template}
          showTitle={false}
          clientName={candidateConsent.client?.name}
          projectName={candidateConsent.project?.name}
        />
      ) : (
        <Table
          bordered
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ marginTop: '10px' }}
          columns={[
            {
              title: 'Status update date',
              dataIndex: 'createdDate',
              render: (createdDate: string) => dayjs(createdDate).format('DD-MM-YYYY'),
              defaultSortOrder: 'descend',
              sorter: (a, b) => a.createdDate.localeCompare(b.createdDate),
              sortDirections: ['ascend', 'descend', 'ascend'],
            },
            {
              title: 'Responsible',
              dataIndex: 'createdBy',
              render: (createdBy: CandidateConsent['createdBy']) =>
                createdBy ? `${createdBy.firstName} ${createdBy.lastName}` : '',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (status: CandidateConsentStatus) => <FormattedCandidateConsentStatus status={status} />,
            },
          ]}
          dataSource={data}
        />
      )}
    </>
  );
};
